/* Быстрая запись: поиск по телефону или номеру, услуги, ближайший свободный слот. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { DAY, conflict, phoneNorm, startOfDay, sum, time, type Order } from "@okno/shared";
import { useActions } from "../../lib/actions";
import { ApiError, api } from "../../lib/api";
import { locKey, useBoard, useDomainCtx, useSession } from "../../lib/session";
import type { CustomerDetails, CustomerRow, SearchHit, SlotsResponse } from "../../lib/types";
import { Modal, useCloseLayer } from "../../ui/layers";
import { useToast } from "../../ui/toast";
import { EmptySlotField, ExtraFields, FixedSlotField, ServicesField, SlotsField, type BookingSource, type SlotPos } from "./BookingParts";
import { ClientSearch, NewClientForm, PickedClient, type NewCustomer } from "./ClientSection";

export interface BookingPrefill {
  customerId?: string;
  vehicleId?: string;
  serviceIds?: string[];
  bayId?: string;
  start?: number;
}

const SLOT_COUNT = 8;
const EMPTY_NEW: NewCustomer = { name: "", phone: "", plate: "", car: "", cls: 1, sms: true };

function normPlate(s: string): string {
  return String(s).toUpperCase().replace(/[^0-9A-ZА-Я]/g, "");
}

export function BookingModal({ prefill }: { prefill?: BookingPrefill }) {
  const { boot, vertical, locationId } = useSession();
  const close = useCloseLayer();
  const toast = useToast();
  const qc = useQueryClient();
  const { openOrder } = useActions();
  const p = prefill || {};

  const [client, setClient] = useState<CustomerRow | null>(null);
  const [vehicleId, setVehicleId] = useState<string | null>(p.vehicleId || null);
  const [isNew, setIsNew] = useState(false);
  const [newC, setNewC] = useState<NewCustomer>(EMPTY_NEW);
  const [sids, setSids] = useState<string[]>(p.serviceIds ? [...p.serviceIds] : []);
  const [fixed, setFixed] = useState(!!(p.bayId && p.start));
  const [slot, setSlot] = useState<SlotPos | null>(p.bayId && p.start ? { bayId: p.bayId, start: p.start } : null);
  const [source, setSource] = useState<BookingSource>("phone");
  const [comment, setComment] = useState("");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  // клиент из карточки клиента/заказа — подгружаем один раз
  const preClient = useQuery({
    queryKey: locKey(locationId, "customer", p.customerId),
    queryFn: () => api.get<CustomerDetails>("/locations/" + locationId + "/customers/" + p.customerId),
    enabled: !!p.customerId,
  });
  useEffect(() => {
    if (!preClient.data) return;
    const d = preClient.data;
    setClient((cur) => cur ?? { customer: d.customer, vehicles: d.vehicles, stats: d.stats });
    setVehicleId((cur) => cur ?? d.vehicles[0]?.id ?? null);
  }, [preClient.data]);

  const vehicle = client?.vehicles.find((v) => v.id === vehicleId);
  const cls = isNew ? newC.cls : vehicle?.cls || 1;

  // цены и длительности под класс авто и ближайшие слоты — с сервера
  const slotsQ = useQuery({
    queryKey: locKey(locationId, "slots", sids, cls),
    queryFn: () => api.post<SlotsResponse>("/locations/" + locationId + "/slots", { serviceIds: sids, cls, count: SLOT_COUNT }),
    enabled: sids.length > 0,
    placeholderData: (prev) => prev,
  });
  const items = sids.length ? slotsQ.data?.items || [] : [];
  const dur = sum(items, (x) => x.dur);

  // для фиксированного слота проверяем конфликт по расписанию дня
  const board = useBoard(startOfDay(slot?.start ?? p.start ?? Date.now()), startOfDay(slot?.start ?? p.start ?? Date.now()) + DAY);
  const ctx = useDomainCtx(board.data);
  const fixedBay = fixed && slot ? boot.bays.find((b) => b.id === slot.bayId) : undefined;
  const cf = fixed && slot && ctx && dur ? conflict(ctx, slot.bayId, slot.start, dur, null, boot.settings.bufferMin) : null;

  const slots = slotsQ.data?.slots || [];
  const chosen: SlotPos | null = fixed ? slot : slots.find((s) => slot && s.start === slot.start && s.bayId === slot.bayId) || slots[0] || null;

  const hasClient = isNew || (!!client && !!vehicleId);
  const ready = hasClient && sids.length > 0 && items.length > 0 && !!chosen && !saving;

  function toggleService(id: string) {
    setSids((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }

  function pickHit(hit: SearchHit) {
    setClient(hit);
    setVehicleId(hit.matchVehicleId ?? hit.vehicles[0]?.id ?? null);
  }

  function clearClient() {
    setClient(null);
    setVehicleId(null);
    setIsNew(false);
  }

  function startNew() {
    const q = query.trim();
    setIsNew(true);
    if (/\d{5,}/.test(q.replace(/\D/g, ""))) setNewC((n) => ({ ...n, phone: q }));
    else if (q) setNewC((n) => ({ ...n, plate: q.toUpperCase() }));
  }

  /** Телефон уже в базе: находим клиента и подставляем его, как в макете. */
  async function adoptExisting(phone: string): Promise<boolean> {
    try {
      const hits = await api.get<SearchHit[]>("/locations/" + locationId + "/customers/search", { q: phone });
      const hit = hits.find((h) => phoneNorm(h.customer.phone) === phone);
      if (!hit) return false;
      setIsNew(false);
      pickHit(hit);
      toast("Клиент с этим телефоном уже есть — выбрали его", "info");
      return true;
    } catch {
      return false;
    }
  }

  function validateNew(): boolean {
    const errors: string[] = [];
    if (!newC.name.trim()) errors.push("имя");
    if (phoneNorm(newC.phone).length !== 11) errors.push("телефон из 11 цифр");
    if (normPlate(newC.plate).length < 5) errors.push("госномер");
    if (errors.length) toast("Заполните: " + errors.join(", "), "bad");
    return !errors.length;
  }

  async function save() {
    if (!ready || !chosen) return;
    if (isNew && !validateNew()) return;
    if (fixed && cf) {
      toast("Не помещается: пост занят с " + time(cf.start) + ". Уберите услугу или выберите другое время", "bad");
      return;
    }
    const who = isNew
      ? { newCustomer: { name: newC.name.trim(), phone: newC.phone, plate: newC.plate.trim(), car: newC.car.trim(), cls: newC.cls, sms: newC.sms } }
      : { customerId: client?.customer.id, vehicleId };
    setSaving(true);
    try {
      const o = await api.post<Order>("/locations/" + locationId + "/orders", {
        ...who,
        serviceIds: sids,
        bayId: chosen.bayId,
        start: chosen.start,
        source,
        comment: comment.trim() || undefined,
      });
      void qc.invalidateQueries({ queryKey: locKey(locationId) });
      close();
      const bay = boot.bays.find((b) => b.id === o.bayId);
      toast("Записали на " + time(o.start) + ", " + (bay?.name || ""), "ok", { label: "Открыть", fn: () => openOrder(o.id) });
    } catch (e) {
      if (e instanceof ApiError && e.code === "customer_exists" && isNew && (await adoptExisting(phoneNorm(newC.phone)))) return;
      if (e instanceof ApiError && e.code === "slot_taken") {
        toast("Слот только что заняли — выберите другое время", "bad");
        setFixed(false);
        setSlot(null);
        void qc.invalidateQueries({ queryKey: locKey(locationId, "slots") });
        void qc.invalidateQueries({ queryKey: locKey(locationId, "board") });
        return;
      }
      toast.error(e);
    } finally {
      setSaving(false);
    }
  }

  const saveLabel = ready && chosen ? "Записать на " + time(chosen.start) : "Записать";

  return (
    <Modal
      title="Новая запись"
      sub="Телефон или госномер → услуги → время"
      foot={
        <>
          <button className="btn" onClick={close}>
            Отмена
          </button>
          <span style={{ flex: 1 }} />
          <button className="btn btn-primary" disabled={!ready} onClick={() => void save()}>
            {saving ? "Записываем…" : saveLabel}
          </button>
        </>
      }
    >
      <div className="stack">
        {client ? (
          <PickedClient client={client} vehicleId={vehicleId} onVehicle={setVehicleId} onClear={clearClient} />
        ) : isNew ? (
          <NewClientForm value={newC} onChange={setNewC} onClear={clearClient} />
        ) : p.customerId && preClient.isLoading ? (
          <p className="muted" style={{ fontSize: 14 }}>
            Загружаем клиента…
          </p>
        ) : (
          <ClientSearch query={query} onQuery={setQuery} onPick={pickHit} onNew={startNew} />
        )}
        <ServicesField services={boot.services} bay={fixedBay || null} vertical={vertical} cls={cls} selected={sids} items={items} onToggle={toggleService} />
        {!sids.length ? (
          <EmptySlotField />
        ) : fixed && slot ? (
          <FixedSlotField
            slot={slot}
            bay={fixedBay}
            dur={dur}
            cf={cf}
            onUnfix={() => {
              setFixed(false);
              setSlot(null);
            }}
          />
        ) : (
          <SlotsField slots={slots} bays={boot.bays} selected={chosen} isLoading={slotsQ.isFetching} isError={slotsQ.isError} onPick={setSlot} />
        )}
        <ExtraFields source={source} comment={comment} onSource={setSource} onComment={setComment} />
      </div>
    </Modal>
  );
}
